import { useState, useRef, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { useLang } from "@/i18n/LangContext";
import { api } from "@/lib/api";
import Spinner from "@/components/Spinner";
import AdBanner from "@/components/AdBanner";

interface TagGame {
  appid: string;
  name: string;
  capsule_image?: string;
  release_date: string;
  price_text?: string;
  final_price_text?: string;
  review_summary: string;
  platforms: { windows: boolean; mac: boolean; linux: boolean };
}

const TAGS = [
  { label: "RPG", value: "RPG" }, { label: "FPS", value: "FPS" }, { label: "오픈 월드", value: "Open World" },
  { label: "로그라이크", value: "Roguelike" }, { label: "생존", value: "Survival" }, { label: "호러", value: "Horror" },
  { label: "퍼즐", value: "Puzzle" }, { label: "전략", value: "Strategy" }, { label: "레이싱", value: "Racing" },
  { label: "스포츠", value: "Sports" }, { label: "시뮬레이션", value: "Simulation" }, { label: "어드벤처", value: "Adventure" },
  { label: "인디", value: "Indie" }, { label: "액션", value: "Action" }, { label: "MMO", value: "MMO" },
  { label: "비주얼 노벨", value: "Visual Novel" }, { label: "협동", value: "Co-op" }, { label: "플랫포머", value: "Platformer" },
  { label: "턴제", value: "Turn-Based" }, { label: "샌드박스", value: "Sandbox" },
];

export default function TagsPage() {
  const { t } = useLang();
  const [selectedTag, setSelectedTag] = useState(TAGS[0].value);
  const [games, setGames] = useState<TagGame[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const startRef = useRef(0);
  const fetchingRef = useRef(false);

  const fetchGames = useCallback(async (tag: string, reset: boolean) => {
    if (fetchingRef.current) return;
    fetchingRef.current = true;
    const start = reset ? 0 : startRef.current;
    if (reset) { setLoading(true); setGames([]); } else setLoadingMore(true);
    try {
      const data = await api.tagGames(tag, "ko", start) as { games?: TagGame[]; results?: TagGame[]; total_count?: number };
      const items = data.games || data.results || [];
      if (data.total_count) setTotalCount(data.total_count);
      if (reset) { setGames(items); startRef.current = items.length; }
      else { setGames((p) => [...p, ...items]); startRef.current += items.length; }
      setHasMore(items.length >= 15);
    } catch { setHasMore(false); }
    setLoading(false); setLoadingMore(false); fetchingRef.current = false;
  }, []);

  useEffect(() => { fetchGames(selectedTag, true); }, [selectedTag, fetchGames]);

  // Infinite scroll
  useEffect(() => {
    const handleScroll = () => {
      const bottom = document.documentElement.scrollHeight - window.scrollY - window.innerHeight;
      if (bottom < 400 && !fetchingRef.current && hasMore) fetchGames(selectedTag, false);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [selectedTag, hasMore, fetchGames]);

  const selectedLabel = TAGS.find((t) => t.value === selectedTag)?.label || selectedTag;

  return (
    <div className="max-w-[1320px] mx-auto px-4 sm:px-6 flex-1 w-full">
      {/* Description card */}
      <div className="bg-card-bg dark:bg-card-bg rounded-2xl border border-gray-200 dark:border-card-border shadow-sm p-4 sm:p-5 mt-6 mb-6">
        <div className="flex items-center gap-2.5 mb-2">
          <div className="w-8 h-8 rounded-lg bg-gray-900 dark:bg-card-border flex items-center justify-center">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2"><path d="M20.59 13.41l-7.17 7.17a2 2 0 01-2.83 0L2 12V2h10l8.59 8.59a2 2 0 010 2.82z" /><line x1="7" y1="7" x2="7.01" y2="7" /></svg>
          </div>
          <h1 className="text-sm sm:text-base font-bold text-gray-900 dark:text-white">{t.tags?.title || "태그별 게임 탐색"}</h1>
        </div>
        <p className="text-xs sm:text-sm text-gray-500 dark:text-muted leading-relaxed">{t.tags?.description || "Steam 태그별로 게임을 탐색하고 원하는 장르의 게임을 찾아보세요."}</p>
      </div>

      {/* Tag selector card */}
      <div className="bg-card-bg dark:bg-card-bg rounded-2xl border border-gray-200 dark:border-card-border shadow-sm p-4 sm:p-5 mt-4 mb-5">
        <p className="text-xs font-semibold text-gray-500 dark:text-muted uppercase tracking-wider mb-3">{t.tags?.selectTag || "태그 선택"}</p>
        <div className="flex flex-wrap gap-2">
          {TAGS.map((tag) => (
            <button key={tag.value} onClick={() => setSelectedTag(tag.value)} className={`px-3.5 py-1.5 rounded-lg text-sm font-medium transition-all duration-150 ${selectedTag === tag.value ? "bg-gray-900 text-white shadow-sm" : "bg-inner-bg dark:bg-inner-bg text-gray-600 dark:text-muted border border-gray-200 dark:border-card-border hover:bg-hover-bg dark:hover:bg-hover-bg hover:text-gray-900 dark:hover:text-white"}`}>{tag.label}</button>
          ))}
        </div>
      </div>

      {/* Results count */}
      <div className="mb-3 px-1">
        <p className="text-sm text-gray-500 dark:text-muted">
          <span className="text-gray-900 dark:text-white font-semibold">{selectedLabel}</span> 태그 게임{" "}
          {totalCount > 0 && <span className="text-gray-900 dark:text-white font-semibold">{totalCount.toLocaleString("ko-KR")}</span>}
          {totalCount > 0 && "개"}
        </p>
      </div>

      {/* Game list — 원본과 동일한 가로 카드 형태 */}
      {loading ? <Spinner /> : games.length === 0 ? (
        <div className="text-center py-12 text-sm text-gray-400 dark:text-sub-text">게임이 없어요</div>
      ) : (
        <div className="flex flex-col gap-2.5 pb-4">
          {games.map((game, idx) => (
            <div key={`${game.appid}-${idx}`}>
              {idx > 0 && idx % 10 === 0 && <AdBanner className="my-2" />}
              <Link to={`/game/${game.appid}`} className="block group bg-card-bg dark:bg-card-bg rounded-xl border border-gray-200 dark:border-card-border overflow-hidden hover:shadow-md hover:border-gray-300 dark:hover:border-card-border transition-all duration-200">
                <div className="flex items-center">
                  <div className="relative shrink-0 w-[130px] h-[60px] sm:w-[220px] sm:h-[102px] bg-hover-bg dark:bg-background overflow-hidden">
                    <img src={game.capsule_image || `https://shared.cloudflare.steamstatic.com/store_item_assets/steam/apps/${game.appid}/header_292x136.jpg`} alt={game.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" loading="lazy" />
                  </div>
                  <div className="flex-1 min-w-0 px-2.5 sm:px-5 py-2 sm:py-3 flex items-center gap-2 sm:gap-4">
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-xs sm:text-[15px] text-gray-900 dark:text-white group-hover:text-blue-600 transition-colors line-clamp-1">{game.name}</h3>
                      <div className="flex items-center gap-1.5 sm:gap-2 mt-1 sm:mt-1.5 overflow-hidden">
                        {game.review_summary && game.review_summary !== "none" && (
                          <span className={`text-[9px] sm:text-[11px] font-semibold px-1 sm:px-1.5 py-0.5 rounded border shrink-0 ${
                            game.review_summary === "positive"
                              ? "text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-500/10 border-emerald-200 dark:border-emerald-500/30"
                              : game.review_summary === "mixed"
                              ? "text-yellow-600 dark:text-yellow-400 bg-yellow-50 dark:bg-yellow-500/10 border-yellow-200 dark:border-yellow-500/30"
                              : "text-red-500 dark:text-red-400 bg-red-50 dark:bg-red-500/10 border-red-200 dark:border-red-500/30"
                          }`}>
                            {game.review_summary === "positive" ? (t.common?.positive || "긍정적") : game.review_summary === "mixed" ? (t.common?.mixed || "복합적") : (t.common?.negative || "부정적")}
                          </span>
                        )}
                        {game.release_date && <span className="text-[10px] sm:text-xs text-gray-400 dark:text-muted hidden sm:inline shrink-0">{game.release_date}</span>}
                        {/* Platform icons */}
                        <span className="hidden sm:flex items-center gap-0.5 text-gray-300 dark:text-muted shrink-0">
                          {game.platforms?.windows && <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><path d="M3 12.5l7-1V4L3 5.5v7zm0 7L10 21v-7.5l-7-1v7zm8-1L21 21V12h-10v6.5zm0-14v7.5H21V3L11 4.5z" /></svg>}
                          {game.platforms?.mac && <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" /></svg>}
                          {game.platforms?.linux && <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><path d="M12.504 0c-.155 0-.311.015-.466.045C9.49.401 7.803 2.926 7.803 5.594c0 .724.147 1.44.436 2.119.294.687.707 1.293 1.228 1.8.112.108.224.215.337.321v3.854H7.803v1.5h2.341v3.854c-.113.104-.225.213-.337.321-.521.507-.934 1.113-1.228 1.8-.289.679-.436 1.395-.436 2.119 0 2.668 1.687 5.193 4.235 5.549.155.03.311.045.466.045s.311-.015.466-.045c2.548-.356 4.235-2.881 4.235-5.549 0-.724-.147-1.44-.436-2.119-.294-.687-.707-1.293-1.228-1.8-.112-.108-.224-.215-.337-.321v-3.854h2.341v-1.5H14.14V9.884c.113-.106.225-.213.337-.321.521-.507.934-1.113 1.228-1.8.289-.679.436-1.395.436-2.119 0-2.668-1.687-5.193-4.235-5.549A3.612 3.612 0 0012.504 0z" /></svg>}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5 sm:gap-2.5 shrink-0">
                      <div className="flex flex-col items-end">
                        <span className="text-xs sm:text-[15px] font-bold text-gray-900 dark:text-white leading-tight">{game.final_price_text || game.price_text || "무료"}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </Link>
            </div>
          ))}
        </div>
      )}

      {loadingMore && <Spinner />}
      {!hasMore && games.length > 0 && <p className="text-center text-[10px] text-gray-400 dark:text-sub-text py-4">모든 게임을 불러왔어요</p>}
      <AdBanner className="my-4" />
    </div>
  );
}
